import React from "react";
import { useForm } from "react-hook-form";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import CountrySelector from "./CountrySelector";
import SocialMediaLinks from "./SocialMediaLinks";
import TipsPreferences from "./TipsPreferences";
import ProfilePicture from "./ProfilePicture";
import FavoriteBrands from "./FavoriteBrands";

type PrivacySetting = 'public' | 'contacts' | 'private';

interface ProfileFormData {
  username: string;
  phone_number: string;
  country_code: string;
  instagram_link: string;
  tiktok_link: string;
  accept_tips: boolean;
  avatar_url: string | null;
  privacy_setting: PrivacySetting;
  favorite_brands: string[];
}

const isValidPrivacySetting = (value: string): value is PrivacySetting => {
  return ['public', 'contacts', 'private'].includes(value);
};

const ProfileSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user?.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { register, handleSubmit, setValue, watch } = useForm<ProfileFormData>({
    defaultValues: {
      username: profile?.username || "",
      phone_number: profile?.phone_number || "",
      country_code: profile?.country_code || "",
      instagram_link: profile?.instagram_link || "",
      tiktok_link: profile?.tiktok_link || "",
      accept_tips: profile?.accept_tips ?? true,
      avatar_url: profile?.avatar_url || null,
      privacy_setting: isValidPrivacySetting(profile?.privacy_setting || 'public') 
        ? (profile?.privacy_setting as PrivacySetting) 
        : 'public',
      favorite_brands: profile?.favorite_brands || [],
    },
  });

  React.useEffect(() => {
    if (profile) {
      setValue("username", profile.username || "");
      setValue("phone_number", profile.phone_number || "");
      setValue("country_code", profile.country_code || "");
      setValue("instagram_link", profile.instagram_link || "");
      setValue("tiktok_link", profile.tiktok_link || "");
      setValue("accept_tips", profile.accept_tips ?? true);
      setValue("avatar_url", profile.avatar_url || null);
      setValue("privacy_setting", isValidPrivacySetting(profile.privacy_setting || 'public') 
        ? (profile.privacy_setting as PrivacySetting) 
        : 'public'
      );
      setValue("favorite_brands", profile.favorite_brands || []);
    }
  }, [profile, setValue]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      console.log("Updating profile with data:", data);
      const { error } = await supabase
        .from("profiles")
        .update({
          username: data.username,
          phone_number: data.phone_number,
          country_code: data.country_code,
          instagram_link: data.instagram_link,
          tiktok_link: data.tiktok_link,
          accept_tips: data.accept_tips,
          avatar_url: data.avatar_url,
          privacy_setting: data.privacy_setting,
          favorite_brands: data.favorite_brands,
        })
        .eq("id", user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
      toast({
        title: t("profile.profileUpdated"),
        description: t("profile.profileUpdateSuccess"),
      });
    },
    onError: (error) => {
      console.error("Error updating profile:", error);
      toast({
        title: t("common.error"),
        description: t("common.errorUpdating"),
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    updateProfileMutation.mutate(data);
  };

  const handleAvatarChange = (url: string | null) => {
    setValue("avatar_url", url);
  };

  if (isLoadingProfile) {
    return <div>Loading profile...</div>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <ProfilePicture
        avatarUrl={watch("avatar_url")}
        username={watch("username")}
        onAvatarChange={handleAvatarChange}
      />

      <div className="space-y-4">
        <div>
          <Label htmlFor="username">{t("common.username")}</Label>
          <Input id="username" {...register("username")} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="country_code">{t("common.countryCode")}</Label>
            <CountrySelector
              value={watch("country_code")}
              onValueChange={(value) => setValue("country_code", value)}
            />
          </div>
          <div>
            <Label htmlFor="phone_number">{t("common.phoneNumber")}</Label>
            <Input id="phone_number" {...register("phone_number")} />
          </div>
        </div>

        <div className="space-y-3">
          <Label>{t("profile.privacySettings")}</Label>
          <RadioGroup
            value={watch("privacy_setting")}
            onValueChange={(value: PrivacySetting) => setValue("privacy_setting", value)}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="public" id="public" />
              <Label htmlFor="public">{t("profile.publicProfile")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="contacts" id="contacts" />
              <Label htmlFor="contacts">{t("profile.contactsOnly")}</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="private" id="private" />
              <Label htmlFor="private">{t("profile.privateProfile")}</Label>
            </div>
          </RadioGroup>
        </div>

        <SocialMediaLinks
          instagramLink={watch("instagram_link")}
          tiktokLink={watch("tiktok_link")}
          onInstagramChange={(e) => setValue("instagram_link", e.target.value)}
          onTiktokChange={(e) => setValue("tiktok_link", e.target.value)}
        />

        <FavoriteBrands
          brands={watch("favorite_brands")}
          onBrandsChange={(brands) => setValue("favorite_brands", brands)}
        />

        <TipsPreferences
          acceptTips={watch("accept_tips")}
          onAcceptTipsChange={(checked) => setValue("accept_tips", checked)}
        />
      </div>

      <Button type="submit" disabled={updateProfileMutation.isPending}>
        {updateProfileMutation.isPending ? t("common.saving") : t("profile.saveChanges")}
      </Button>
    </form>
  );
};

export default ProfileSettings;